import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Play, Square, Copy, RefreshCw, Upload, X } from 'lucide-react';
import * as imageService from '../services/imageService';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';
const RELAY_URL = 'https://inference.heavyweight.work';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const PIPELINE_LABELS = {
  'text-generation': 'LLM',
  'text-to-image': 'Image',
  'image-text-to-text': 'Vision',
  'text-to-video': 'Video',
  'automatic-speech-recognition': 'ASR',
  'feature-extraction': 'Embed',
  'text-to-speech': 'TTS',
  'conversational': 'Chat',
  'misc': 'Misc',
};

// Category presets — must match ClusterSharingModal definitions
const CATEGORY_PRESETS = {
  'text-generation': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'system_prompt', label: 'System Prompt', type: 'textarea' },
    { key: 'temperature', label: 'Temperature', type: 'slider', default: 0.7, min: 0, max: 2, step: 0.1 },
    { key: 'max_tokens', label: 'Max Tokens', type: 'number', default: 512, min: 1, max: 8192 },
    { key: 'top_p', label: 'Top P', type: 'slider', default: 0.9, min: 0, max: 1, step: 0.05 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'text-to-image': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'negative_prompt', label: 'Negative Prompt', type: 'textarea' },
    { key: 'width', label: 'Width', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'height', label: 'Height', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'steps', label: 'Steps', type: 'number', default: 20, min: 1, max: 150 },
    { key: 'guidance_scale', label: 'Guidance Scale', type: 'slider', default: 7.5, min: 0, max: 30, step: 0.5 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'image-text-to-text': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'image', label: 'Image', type: 'image' },
    { key: 'max_tokens', label: 'Max Tokens', type: 'number', default: 512, min: 1, max: 8192 },
    { key: 'temperature', label: 'Temperature', type: 'slider', default: 0.7, min: 0, max: 2, step: 0.1 },
  ],
  'text-to-video': [
    { key: 'prompt', label: 'Prompt', type: 'textarea' },
    { key: 'image_input', label: 'Image', type: 'image' },
    { key: 'video_input', label: 'Video', type: 'video' },
    { key: 'audio_input', label: 'Audio', type: 'audio' },
    { key: 'width', label: 'Width', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'height', label: 'Height', type: 'number', default: 1024, min: 64, max: 4096 },
    { key: 'fps', label: 'FPS', type: 'number', default: 24, min: 1, max: 120 },
    { key: 'num_frames', label: 'Number of Frames', type: 'number', default: 24, min: 1, max: 300 },
    { key: 'steps', label: 'Steps', type: 'number', default: 20, min: 1, max: 150 },
    { key: 'seed', label: 'Seed', type: 'number' },
  ],
  'automatic-speech-recognition': [
    { key: 'audio', label: 'Audio', type: 'audio' },
    { key: 'language', label: 'Language', type: 'text' },
  ],
  'misc': [],
};

// Convert new config format (mappings/defaults/jsonTemplate) to old inputs format
const convertConfigToInputs = (config) => {
  if (!config) return null;
  // If config has a jsonTemplate or output, always go through the new format path
  // even if it also has an old inputs array (hybrid config from OpenAPI smart inference)
  if (config.inputs && Array.isArray(config.inputs) && !config.jsonTemplate && !config.output) return config;

  const presets = CATEGORY_PRESETS[config.category] || [];
  const mappings = config.mappings || {};
  const defaults = config.defaults || {};
  // Per-config starting values, editable in the playground. Distinct from
  // `defaults`, which hardcodes an unmapped API field and hides it entirely.
  // Two maps because the two input kinds have different stable identities: a
  // mapped preset is keyed by preset key, a template variable by its name in
  // the template. One map would make `seed` the preset and `SEED` the variable
  // collide the moment someone lowercased a variable.
  const inputDefaults = config.inputDefaults || {};
  const jsonVarDefaults = config.jsonVarDefaults || {};
  const jsonTemplate = config.jsonTemplate || null;
  const inputs = [];

  // Determine which preset keys are consumed by JSON template variables
  const jsonConsumedKeys = new Set();
  if (jsonTemplate?.variableMap) {
    for (const presetKey of Object.values(jsonTemplate.variableMap)) {
      jsonConsumedKeys.add(presetKey);
    }
  }

  // Add mapped presets as visible inputs (skip JSON-consumed ones)
  for (const preset of presets) {
    const apiFieldName = mappings[preset.key];
    if (!apiFieldName) continue;
    if (jsonConsumedKeys.has(preset.key)) continue;

    inputs.push({
      name: apiFieldName,
      label: preset.label,
      type: preset.type,
      required: ['prompt'].includes(preset.key),
      min: preset.min,
      max: preset.max,
      step: preset.step,
      // A per-config default wins over the category catalogue's.
      default: inputDefaults[preset.key] !== undefined
        ? inputDefaults[preset.key]
        : preset.default,
      options: preset.options,
    });
  }

  // Add JSON template variable inputs (these feed into the template, not sent directly)
  if (jsonTemplate?.template && jsonTemplate?.targetField) {
    const varMap = jsonTemplate.variableMap || {};
    for (const [varName, presetKey] of Object.entries(varMap)) {
      const preset = presets.find(p => p.key === presetKey);
      inputs.push({
        name: `__json_var__${varName}`,
        label: preset?.label || varName.charAt(0).toUpperCase() + varName.slice(1).toLowerCase(),
        type: preset?.type || (['prompt', 'system_prompt'].includes(presetKey) ? 'textarea' : 'text'),
        required: ['prompt'].includes(presetKey),
        // Bounds come from the preset so a numeric variable renders as a real
        // number field or slider rather than an unbounded box.
        min: preset?.min,
        max: preset?.max,
        step: preset?.step,
        options: preset?.options,
        // Template variables had no default at all, so every workflow field --
        // seed, steps, fps, width, height -- started empty and substituted as
        // "" into the JSON. A per-config default fixes that; the catalogue
        // default is the fallback.
        default: jsonVarDefaults[varName] !== undefined
          ? jsonVarDefaults[varName]
          : preset?.default,
        _jsonVar: varName,
      });
    }
  }

  // Add defaults as hardcoded values (skip nulls — those are "skipped" fields)
  for (const [fieldName, value] of Object.entries(defaults)) {
    if (value === null || value === undefined) continue;
    // Don't add if already mapped as a visible input
    if (Object.values(mappings).includes(fieldName)) continue;
    // Don't add if this field is the jsonTemplate target (filled by the template)
    if (jsonTemplate?.targetField === fieldName) continue;
    const isBool = typeof value === 'boolean' || value === 'true' || value === 'false';
    inputs.push({
      name: fieldName,
      type: isBool ? 'toggle' : typeof value === 'number' ? 'number' : 'text',
      hardcoded: value,
    });
  }

  return {
    ...config,
    inputs,
    _jsonTemplate: jsonTemplate,
    _output: config.output || { format: 'object', type: 'auto' },
  };
};

// NATS routing tokens after the MODEL token, in subject order. A playground
// config may pin any of these (region / GPU / provider); blank/`*` means "any".
// The relay resolves the resulting wildcard subject to a live worker.
const ROUTING_ORDER = ['type', 'continent', 'country', 'provider', 'accelerator'];

const sanitizeRoutingToken = (v) => {
  const s = (v ?? '').toString().trim();
  if (!s || s === '*') return '*';
  return s.toUpperCase().replace(/[^A-Z0-9_*>]/g, '_');
};

// Compose the effective `model` string sent to the relay. When a config pins
// one or more routing dimensions, append the 5 constraint tokens to the base
// subject (e.g. `IW3_EXP.*.EU.*.*.RTX4090`); otherwise send the base as-is.
const effectiveModelId = (model) => {
  const routing = model?.playgroundConfig?.routing;
  if (!routing) return model.fullId;
  const tokens = ROUTING_ORDER.map(k => sanitizeRoutingToken(routing[k]));
  if (tokens.every(t => t === '*')) return model.fullId;
  return [model.fullId, ...tokens].join('.');
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'text-generation', label: 'Text Gen' },
  { key: 'text-to-image', label: 'Image' },
  { key: 'image-text-to-text', label: 'Vision' },
  { key: 'text-to-video', label: 'Video' },
  { key: 'automatic-speech-recognition', label: 'ASR' },
];

/**
 * An <img> that becomes a <video> when the bytes turn out not to be an image.
 *
 * Auto-detection keys off the URL extension, and an output URL does not always
 * have one -- a proxied prediction output used to be extension-less, so a
 * generated video rendered as a broken image showing its alt text ("output-0").
 * The relay now puts the extension back, but a model can return any URL it
 * likes, so the renderer recovers on its own rather than trusting the path.
 */
const OutputMedia = ({ src, index }) => {
  const [fallback, setFallback] = useState(null);
  const cls = 'max-w-full rounded-lg border border-gray-200 mb-2';

  if (fallback === 'video') {
    return (
      <video src={src} controls autoPlay loop className={cls}
             onError={() => setFallback('link')} />
    );
  }
  if (fallback === 'link') {
    return (
      <a href={src} target="_blank" rel="noreferrer"
         className="block text-xs font-mono text-blue-600 underline break-all mb-2">
        {src}
      </a>
    );
  }
  return (
    <img src={src} alt={`output-${index}`} className={cls}
         onError={() => setFallback('video')} />
  );
};

const PlaygroundTab = () => {
  const [models, setModels] = useState([]);
  const [tcfModels, setTcfModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [serverStatus, setServerStatus] = useState({ status: 'checking', text: 'Checking...' });
  const [metrics, setMetrics] = useState(null);

  // Playground configs from curated images
  const [playgroundConfigs, setPlaygroundConfigs] = useState({});

  // Chat state
  const [systemMsg, setSystemMsg] = useState('');
  const [userMsg, setUserMsg] = useState('');
  const [response, setResponse] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [responseMeta, setResponseMeta] = useState(null);
  const [apiSnippetCopied, setApiSnippetCopied] = useState(false);
  const [inferenceError, setInferenceError] = useState(null);
  const [activeMode, setActiveMode] = useState('chat'); // chat or code

  // Custom inputs for playground-configured models
  const [customInputs, setCustomInputs] = useState({});

  // Prediction tracker
  const [predictionStatus, setPredictionStatus] = useState(null);
  const [predictionTimer, setPredictionTimer] = useState(0);

  // Parameters
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [topP, setTopP] = useState(0.95);
  const [streaming, setStreaming] = useState(true);

  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const chatAreaRef = useRef(null);
  // Tracks the in-flight job so cancel can hit the real server-side endpoint.
  // { type: 'prediction' | 'chat', id: string }
  const currentJobRef = useRef(null);

  // Fetch curated images for playground configs
  // Returns { imageName: [convertedConfig, ...] } — multiple configs per image
  const fetchPlaygroundConfigs = useCallback(async () => {
    try {
      const data = await imageService.listCuratedImages();
      if (data.success && data.images) {
        const configs = {};
        for (const img of data.images) {
          const pgc = img.playground_config;
          const enabledConfigs = [];

          if (Array.isArray(pgc)) {
            for (const c of pgc) {
              if (c.enabled) {
                const converted = convertConfigToInputs(c);
                if (converted) enabledConfigs.push(converted);
              }
            }
          } else if (pgc && typeof pgc === 'object' && pgc.enabled) {
            const converted = convertConfigToInputs(pgc);
            if (converted) enabledConfigs.push(converted);
          }

          if (enabledConfigs.length > 0) {
            configs[img.name.toLowerCase()] = enabledConfigs;
          }
        }
        setPlaygroundConfigs(configs);
        return configs;
      }
    } catch { /* ignore */ }
    return {};
  }, []);

  // Fetch TCF backend models via worker proxy
  // Each NATS model expands to N entries if it has N playground configs
  const fetchTCFModels = useCallback(async (configs) => {
    try {
      const res = await fetch(`${API_URL}/playground/v1/models`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return [];
      const data = await res.json();
      const models = [];
      for (const m of (data.data || [])) {
        const modelName = m.id.split('/').pop();
        const pgConfigs = configs?.[modelName.toLowerCase()] || [];

        if (pgConfigs.length > 0) {
          // Create one entry per enabled config
          for (let i = 0; i < pgConfigs.length; i++) {
            const pgConfig = pgConfigs[i];
            models.push({
              id: pgConfigs.length > 1 ? `${m.id}__cfg${i}` : m.id,
              name: modelName,
              fullId: m.id,
              author: m.id.split('/')[0],
              pipeline: pgConfig.category || m.task || 'text-to-video',
              downloads: 0,
              likes: 0,
              pricing: m.pricing,
              videoPricing: m.video_pricing || null,
              contextLength: m.context_length,
              defaultSystemPrompt: m.default_system_prompt || null,
              workerCount: m.worker_count || 0,
              playgroundConfig: pgConfig,
              _tcf: true,
            });
          }
        }
        // A live worker with no playground config is deliberately NOT listed:
        // without a config there are no input definitions, so the panel could
        // only offer a bare prompt box and no category. The playground shows
        // what has actually been configured for it — configure the image in
        // Playground Config to make it appear here.
      }
      return models;
    } catch {
      return [];
    }
  }, []);

  /**
   * Models shown in the playground: TCF images that have a playground config,
   * and nothing else.
   *
   * This used to merge the Hugging Face catalogue, which listed thousands of
   * models that cannot run here. The playground only drives TCF workers, so a
   * model you cannot launch is noise.
   */
  const fetchModels = useCallback(async (pipelineTag = 'all', configs) => {
    setLoading(true);
    try {
      const all = await fetchTCFModels(configs || playgroundConfigs);
      setTcfModels(all);
      const shown = pipelineTag !== 'all'
        ? all.filter(m => matchesPipeline(m.pipeline, pipelineTag))
        : all;
      setModels(shown);
      if (!selectedModel && shown.length > 0) {
        handleSelectModel(shown[0]);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchTCFModels, selectedModel, playgroundConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  const matchesPipeline = (pipeline, filter) => {
    if (filter === 'all') return true;
    if (pipeline === filter) return true;
    if (filter === 'text-generation' && pipeline === 'conversational') return true;
    return false;
  };

  // Check server status via worker proxy
  const checkServerStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/playground/v1/models`, {
        headers: getAuthHeaders(),
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) {
        setServerStatus({ status: 'offline', text: 'Offline' });
        return;
      }
      const data = await res.json();
      const workerCount = (data.data || []).reduce((sum, m) => sum + (m.worker_count || 0), 0);
      const modelsMap = {};
      for (const m of (data.data || [])) {
        modelsMap[m.id] = { active_workers: m.worker_count || 0 };
      }
      setMetrics({ total_workers: workerCount, models: modelsMap });
      // Update worker counts on existing tcfModels
      setTcfModels(prev => prev.map(tm => {
        const fresh = modelsMap[tm.fullId];
        return fresh ? { ...tm, workerCount: fresh.active_workers } : tm;
      }));
      setServerStatus({ status: 'ready', text: `${workerCount} workers` });
    } catch {
      setServerStatus({ status: 'offline', text: 'Offline' });
    }
  }, []);

  useEffect(() => {
    (async () => {
      const configs = await fetchPlaygroundConfigs();
      fetchModels(activeFilter, configs);
    })();
    checkServerStatus();
    const interval = setInterval(checkServerStatus, 10000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchModels(activeFilter);
  }, [activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize custom inputs from playground config defaults
  const initCustomInputs = (pgConfig) => {
    if (!pgConfig?.inputs) return {};
    const vals = {};
    for (const inp of pgConfig.inputs) {
      if (inp.hardcoded !== undefined && inp.hardcoded !== null && inp.hardcoded !== '') continue;
      if (inp.default !== undefined && inp.default !== null && inp.default !== '') {
        vals[inp.name] = inp.type === 'number' || inp.type === 'slider' ? parseFloat(inp.default) : inp.type === 'toggle' ? inp.default === 'true' || inp.default === true : inp.default;
      } else if (inp.type === 'toggle') {
        vals[inp.name] = false;
      } else {
        vals[inp.name] = '';
      }
    }
    return vals;
  };

  const handleSelectModel = (model) => {
    setSelectedModel(model);
    setResponse('');
    setResponseMeta(null);
    setPredictionStatus(null);
    if (model.defaultSystemPrompt) setSystemMsg(model.defaultSystemPrompt);
    if (model.playgroundConfig) {
      setCustomInputs(initCustomInputs(model.playgroundConfig));
    } else {
      setCustomInputs({});
    }
  };

  const updateCustomInput = (name, value) => {
    setCustomInputs(prev => ({ ...prev, [name]: value }));
  };

  // File upload helper → base64
  const handleFileInput = (name, file) => {
    if (!file) { updateCustomInput(name, ''); return; }
    const reader = new FileReader();
    reader.onload = () => updateCustomInput(name, reader.result);
    reader.readAsDataURL(file);
  };

  // Get the active playground config for the selected model
  const getActiveConfig = () => selectedModel?.playgroundConfig || null;

  // Check if we can run inference
  const canRun = () => {
    if (!selectedModel || isRunning) return false;
    const cfg = getActiveConfig();
    if (cfg) {
      const visibleInputs = cfg.inputs.filter(inp => inp.hardcoded === undefined || inp.hardcoded === null || inp.hardcoded === '');
      const requiredInputs = visibleInputs.filter(inp => inp.required);
      return requiredInputs.every(inp => {
        const val = customInputs[inp.name];
        return val !== undefined && val !== null && val !== '';
      });
    }
    return userMsg.trim().length > 0;
  };

  // Build prediction input body from playground config
  /**
   * Input map for the API example.
   *
   * Differs from buildPredictionInput in one way: a field the user has not
   * filled stays as a visible `$(FIELD)` placeholder instead of being dropped.
   * The example is meant to be a template you complete, so silently omitting
   * empty fields would hide which parameters the model actually accepts.
   */
  /**
   * Render a config's JSON template with the user's variable values.
   *
   * Null when the config has no template. `placeholders` keeps an unfilled
   * variable visible as $(NAME) rather than collapsing it to an empty string:
   * right for the copy-pasteable snippet, wrong for the real call, which must
   * send exactly what the form holds.
   */
  const renderJsonTemplate = (cfg, { placeholders = false } = {}) => {
    const tpl = cfg?._jsonTemplate;
    if (!tpl?.template || !tpl?.targetField) return null;

    let rendered = tpl.template;
    for (const [varName] of Object.entries(tpl.variableMap || {})) {
      const raw = customInputs[`__json_var__${varName}`];
      const filled = raw !== undefined && raw !== null && raw !== '';
      const value = filled
        ? String(raw)
        : (placeholders ? `$(${String(varName).toUpperCase()})` : '');
      const escaped = value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      const pattern = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // One pass, matching the already-quoted form first, then the bare one
      // (e.g. "text": $(PROMPT)) -- either way the value ends up quoted, which
      // is what makes the result valid JSON.
      //
      // It must be a SINGLE replace: a pass never rescans its own output, and
      // in placeholder mode the substituted value is itself $(NAME). Two
      // sequential replaces made the second one re-match what the first had
      // just inserted and produced ""$(PROMPT)"". A function replacer is used
      // so no '$' in the value is read as a replacement pattern.
      rendered = rendered.replace(
        new RegExp(`"\\$\\(${pattern}\\)"|\\$\\(${pattern}\\)`, 'g'),
        () => `"${escaped}"`
      );
    }
    return { field: tpl.targetField, rendered };
  };

  /**
   * Quote a payload for curl's -d, which we wrap in single quotes.
   *
   * An apostrophe in a prompt -- or anywhere inside an embedded workflow --
   * would otherwise close the shell string and leave the user with a command
   * that does not run. '\'' is the standard way to pass one through.
   */
  const shellSingleQuote = (value) => `'${String(value).replace(/'/g, `'\\''`)}'`;

  /**
   * Coerce a hardcoded config value to the type the API expects.
   *
   * Config values arrive as strings from the editor's form fields, but cog
   * validates against the OpenAPI schema and rejects `"false"` where a boolean
   * is declared. Shared with buildSnippetInput so the panel prints the types
   * the real call sends -- printing `"false"` there once cost a caller three
   * requests that cog answered 422 to.
   */
  const coerceHardcoded = (inp) => {
    const hv = inp.hardcoded;
    if (inp.type === 'number' || inp.type === 'slider') return parseFloat(hv);
    if (inp.type === 'toggle' || hv === true || hv === false || hv === 'true' || hv === 'false') {
      return hv === 'true' || hv === true;
    }
    if (typeof hv === 'number' || typeof hv === 'boolean') return hv;
    if (typeof hv === 'string' && hv.match(/^\d+(\.\d+)?$/) && inp.type !== 'text') {
      return parseFloat(hv);
    }
    return hv;
  };

  const buildSnippetInput = (cfg) => {
    const input = {};
    for (const inp of (cfg.inputs || [])) {
      if (inp._jsonVar) continue;
      const hardcoded = inp.hardcoded !== undefined && inp.hardcoded !== null && inp.hardcoded !== '';
      if (hardcoded) {
        input[inp.name] = coerceHardcoded(inp);
        continue;
      }
      const val = customInputs[inp.name];
      input[inp.name] = (val !== undefined && val !== null && val !== '')
        ? val
        : `$(${String(inp.name).toUpperCase()})`;
    }

    // The _jsonVar inputs skipped above do not travel on their own -- they are
    // substituted into the config's JSON template, which is what actually goes
    // on the wire (a ComfyUI workflow, for instance). Leaving it out made the
    // panel show a call that could be copied and would not reproduce the run.
    const tpl = renderJsonTemplate(cfg, { placeholders: true });
    if (tpl) input[tpl.field] = tpl.rendered;

    return input;
  };

  const buildPredictionInput = (cfg) => {
    const input = {};
    for (const inp of (cfg.inputs || [])) {
      // Skip JSON template variable inputs — they feed into the template below
      if (inp._jsonVar) continue;

      if (inp.hardcoded !== undefined && inp.hardcoded !== null && inp.hardcoded !== '') {
        input[inp.name] = coerceHardcoded(inp);
      } else {
        const val = customInputs[inp.name];
        if (val !== undefined && val !== null && val !== '') {
          input[inp.name] = val;
        }
      }
    }

    // Substitute the JSON template and send it to its target field. It goes as
    // a JSON *string*, not a parsed object -- Cog workers expect workflow_json
    // that way. Shared with buildSnippetInput so the API panel cannot drift
    // from what is actually sent.
    const tpl = renderJsonTemplate(cfg);
    if (tpl) input[tpl.field] = tpl.rendered;

    return input;
  };

  // Format output based on output config type
  const formatOutputItem = (val, type) => {
    if (type === 'skip') return '';
    if (type === 'image' || type === 'image_gallery') {
      if (typeof val === 'string' && (val.startsWith('http') || val.startsWith('data:'))) return `![output](${val})`;
      if (Array.isArray(val)) return val.filter(u => typeof u === 'string').map((u, i) => `![output-${i}](${u})`).join('\n\n');
    }
    if (type === 'video' || type === 'video_gallery') {
      if (typeof val === 'string' && (val.startsWith('http') || val.startsWith('data:'))) return `<video_url>${val}</video_url>`;
      if (Array.isArray(val)) return val.filter(u => typeof u === 'string').map(u => `<video_url>${u}</video_url>`).join('\n\n');
    }
    if (type === 'audio') {
      if (typeof val === 'string' && (val.startsWith('http') || val.startsWith('data:'))) return `<audio_url>${val}</audio_url>`;
    }
    if (type === 'text') return typeof val === 'string' ? val : JSON.stringify(val, null, 2);
    // auto: guess from content
    if (typeof val === 'string' && (val.startsWith('http') || val.startsWith('data:'))) {
      // A data: URL states its type outright -- no need to guess.
      if (val.startsWith('data:video/')) return `<video_url>${val}</video_url>`;
      if (val.startsWith('data:audio/')) return `<audio_url>${val}</audio_url>`;
      const ext = (val.split('?')[0].match(/\.([a-z0-9]{2,5})$/i) || [, ''])[1].toLowerCase();
      if (['mp4', 'webm', 'mov', 'mkv', 'm4v', 'avi'].includes(ext)) return `<video_url>${val}</video_url>`;
      if (['mp3', 'wav', 'flac', 'ogg', 'oga', 'm4a', 'aac'].includes(ext)) return `<audio_url>${val}</audio_url>`;
      return `![output](${val})`;
    }
    if (typeof val === 'string') return val;
    return JSON.stringify(val, null, 2);
  };

  const formatOutput = (output, outputCfg) => {
    if (!outputCfg || !outputCfg.format || outputCfg.type === 'auto') {
      // Fallback: auto-detect
      if (typeof output === 'string') return formatOutputItem(output, 'auto');
      if (Array.isArray(output) && output.length > 0 && typeof output[0] === 'string' && (output[0].startsWith('http') || output[0].startsWith('data:'))) {
        return output.map((url, i) => formatOutputItem(url, 'auto')).join('\n\n');
      }
      return JSON.stringify(output, null, 2);
    }
    if (outputCfg.format === 'object') {
      return formatOutputItem(output, outputCfg.type || 'auto');
    }
    if (outputCfg.format === 'array' && Array.isArray(output)) {
      const items = outputCfg.items || [];
      return output.map((val, idx) => {
        const itemType = items[idx]?.type || 'auto';
        if (itemType === 'skip') return null;
        return formatOutputItem(val, itemType);
      }).filter(Boolean).join('\n\n');
    }
    return JSON.stringify(output, null, 2);
  };

  // Run inference
  const runInference = async () => {
    if (!canRun()) return;

    setIsRunning(true);
    setResponse('');
    setResponseMeta(null);
    setInferenceError(null);
    setPredictionStatus({ step: 'queued' });
    setPredictionTimer(0);

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setPredictionTimer(((Date.now() - startTime) / 1000).toFixed(1));
    }, 100);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const cfg = getActiveConfig();
      const isTextModel = ['text-generation', 'conversational'].includes(selectedModel.pipeline);

      if (isTextModel && !cfg) {
        // Standard text model without custom config
        setPredictionStatus({ step: 'processing' });
        const messages = [];
        if (systemMsg.trim()) messages.push({ role: 'system', content: systemMsg });
        messages.push({ role: 'user', content: userMsg });

        const body = {
          model: effectiveModelId(selectedModel),
          messages,
          temperature,
          max_tokens: maxTokens,
          top_p: topP,
          stream: streaming,
        };

        const res = await fetch(`${API_URL}/playground/v1/chat/completions`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        // Track the inference id so cancel can signal the server (worker drops
        // the cog connection mid-stream to free the GPU).
        const chatId = res.headers.get('Inference-Id');
        if (chatId) currentJobRef.current = { type: 'chat', id: chatId };

        if (streaming) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let fullText = '';
          let tokensOut = 0;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n').filter(l => l.startsWith('data: '));
            for (const line of lines) {
              const data = line.slice(6);
              if (data === '[DONE]') continue;
              try {
                const json = JSON.parse(data);
                const delta = json.choices?.[0]?.delta?.content || '';
                fullText += delta;
                tokensOut++;
                setResponse(fullText);
              } catch { /* skip malformed */ }
            }
          }

          const elapsed = (Date.now() - startTime) / 1000;
          setResponseMeta({
            latency: elapsed.toFixed(1) + 's',
            tokensOut,
            tps: (tokensOut / elapsed).toFixed(1),
          });
        } else {
          const data = await res.json();
          const content = data.choices?.[0]?.message?.content || '';
          setResponse(content);
          const elapsed = (Date.now() - startTime) / 1000;
          const usage = data.usage || {};
          setResponseMeta({
            latency: elapsed.toFixed(1) + 's',
            tokensIn: usage.prompt_tokens,
            tokensOut: usage.completion_tokens,
            tps: usage.completion_tokens ? (usage.completion_tokens / elapsed).toFixed(1) : '--',
          });
        }
      } else {
        // Prediction-based model (non-text, or text with custom config)
        setPredictionStatus({ step: 'queued' });
        const input = cfg ? buildPredictionInput(cfg) : { prompt: userMsg };
        const body = { model: effectiveModelId(selectedModel), input };

        const res = await fetch(`${API_URL}/playground/v1/predictions`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // Async: the relay mints a unique prediction id and returns 202.
        const created = await res.json();
        const predictionId = created.id;
        if (!predictionId) throw new Error('No prediction id returned');
        currentJobRef.current = { type: 'prediction', id: predictionId };

        // Poll status until the job reaches a terminal state.
        const outputCfg = cfg?._output || {};
        let final = created;
        // Poll with backoff. A render can run for hours, and a fixed 1s tick
        // would be thousands of requests for one prediction. Stay responsive
        // for the common short job, then ease off: 1s for the first 30
        // polls (~30s), then grow to a 5s ceiling.
        let polls = 0;
        const nextDelayMs = () => (polls < 30 ? 1000 : Math.min(5000, 1000 + (polls - 30) * 250));
        while (true) {
          if (controller.signal.aborted) break;
          await new Promise((r) => setTimeout(r, nextDelayMs()));
          polls += 1;
          const sres = await fetch(
            `${API_URL}/playground/v1/predictions/${encodeURIComponent(predictionId)}`,
            { headers: getAuthHeaders(), signal: controller.signal }
          );
          if (!sres.ok) throw new Error(`HTTP ${sres.status}`);
          final = await sres.json();
          // Map relay statuses onto the UI stepper's vocabulary.
          if (final.status === 'starting') setPredictionStatus({ step: 'queued' });
          else if (final.status === 'processing') setPredictionStatus({ step: 'processing' });
          if (['succeeded', 'failed', 'canceled'].includes(final.status)) break;
        }

        if (controller.signal.aborted) return;
        if (final.status === 'failed') {
          throw new Error(final.error || 'Prediction failed');
        }
        if (final.status === 'canceled') {
          setResponse('');
          setPredictionStatus({ step: 'error' });
          return;
        }
        const output = final.output != null ? final.output : final;
        setResponse(formatOutput(output, outputCfg));
      }

      setPredictionStatus({ step: 'completed' });
    } catch (err) {
      if (err.name !== 'AbortError') {
        const msg = err.message || '';
        const status = parseInt(msg.replace(/\D/g, '')) || 0;
        let friendly;
        if (status === 502 || status === 504 || msg.toLowerCase().includes('timeout')) {
          friendly = 'The model timed out — it may still be loading weights. This is normal on first run or after idle.';
        } else if (status === 503) {
          friendly = 'The model is currently unavailable — it may be starting up.';
        } else if (status === 402) {
          friendly = 'Insufficient credits. Please add credits to continue.';
        } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
          friendly = 'Network error — check your connection and try again.';
        } else {
          friendly = msg;
        }
        setInferenceError({ message: friendly, retryable: status !== 402 });
        setResponse('');
        setPredictionStatus({ step: 'error' });
      }
    } finally {
      setIsRunning(false);
      clearInterval(timerRef.current);
      abortRef.current = null;
      currentJobRef.current = null;
    }
  };

  const cancelInference = async () => {
    // Signal the server so the GPU is actually freed (not just the browser's
    // own request aborted). Then abort the local fetch/poll loop.
    const job = currentJobRef.current;
    if (job?.id) {
      const url = job.type === 'chat'
        ? `${API_URL}/playground/v1/chat/completions/${encodeURIComponent(job.id)}/cancel`
        : `${API_URL}/playground/v1/predictions/${encodeURIComponent(job.id)}/cancel`;
      try {
        await fetch(url, { method: 'POST', headers: getAuthHeaders() });
      } catch (e) {
        console.error('cancel error:', e);
      }
    }
    abortRef.current?.abort();
    setIsRunning(false);
    clearInterval(timerRef.current);
    setPredictionStatus(null);
  };


  /**
   * The same call this panel just made, as copy-pasteable curl.
   *
   * Deliberately targets the TCF API with `Authorization: Bearer <api key>`
   * rather than the relay directly: a user's sk-tcf- key authenticates here,
   * while the relay sits behind Cloudflare Access and would reject it. The
   * body is built from the live form state, so what is shown is what the
   * playground actually sends — not an idealised example that drifts.
   */
  const generateApiSnippet = () => {
    if (!selectedModel) return '';
    const cfg = getActiveConfig();
    const routedModel = effectiveModelId(selectedModel);
    const isTextModel = ['text-generation', 'conversational'].includes(selectedModel.pipeline);

    if (isTextModel && !cfg) {
      const msgs = [];
      if (systemMsg.trim()) msgs.push({ role: 'system', content: systemMsg });
      msgs.push({ role: 'user', content: userMsg || '$(PROMPT)' });
      const body = {
        model: routedModel, messages: msgs,
        temperature, max_tokens: maxTokens, stream: streaming,
      };
      return `# Chat completion — returns the answer in one response
curl -X POST ${API_URL}/playground/v1/chat/completions \\
  -H "Authorization: Bearer <api key>" \\
  -H "Content-Type: application/json" \\
  -d ${shellSingleQuote(JSON.stringify(body, null, 2))}`;
    }

    const input = cfg ? buildSnippetInput(cfg) : { prompt: userMsg || '$(PROMPT)' };
    const body = { model: routedModel, input };
    return `# 1. Start the prediction — returns immediately with an id
curl -X POST ${API_URL}/playground/v1/predictions \\
  -H "Authorization: Bearer <api key>" \\
  -H "Content-Type: application/json" \\
  -d ${shellSingleQuote(JSON.stringify(body, null, 2))}

# 2. Poll until status is succeeded / failed / canceled.
#    A long render can stay "processing" for a while — that is normal,
#    so poll rather than assuming a timeout means failure.
curl ${API_URL}/playground/v1/predictions/PREDICTION_ID \\
  -H "Authorization: Bearer <api key>"

# 3. Cancel it if you no longer want the result (you are billed for
#    the time already consumed).
curl -X POST ${API_URL}/playground/v1/predictions/PREDICTION_ID/cancel \\
  -H "Authorization: Bearer <api key>"`;
  };

  // Generate code snippets
  const generateCurl = () => {
    if (!selectedModel) return '';
    const cfg = getActiveConfig();
    const routedModel = effectiveModelId(selectedModel);
    const isTextModel = ['text-generation', 'conversational'].includes(selectedModel.pipeline);

    if (isTextModel && !cfg) {
      const msgs = [];
      if (systemMsg.trim()) msgs.push(`{"role":"system","content":"${systemMsg.replace(/"/g, '\\"')}"}`);
      msgs.push(`{"role":"user","content":"${(userMsg || '...').replace(/"/g, '\\"')}"}`);
      return `curl -X POST ${RELAY_URL}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "CF-Access-Client-Id: YOUR_CLIENT_ID" \\
  -H "CF-Access-Client-Secret: YOUR_CLIENT_SECRET" \\
  -d '{
    "model": "${routedModel}",
    "messages": [${msgs.join(',')}],
    "temperature": ${temperature},
    "max_tokens": ${maxTokens},
    "stream": ${streaming}
  }'`;
    }

    const input = cfg ? buildPredictionInput(cfg) : { prompt: userMsg || '...' };
    return `# 1. Create the prediction — returns a unique id (status: "starting")
curl -X POST ${RELAY_URL}/v1/predictions \\
  -H "Content-Type: application/json" \\
  -H "CF-Access-Client-Id: YOUR_CLIENT_ID" \\
  -H "CF-Access-Client-Secret: YOUR_CLIENT_SECRET" \\
  -d ${shellSingleQuote(JSON.stringify({ model: routedModel, input }, null, 2))}
# => {"id": "PREDICTION_ID", "status": "starting", ...}

# 2. Poll status until terminal (succeeded / failed / canceled)
curl ${RELAY_URL}/v1/predictions/PREDICTION_ID \\
  -H "CF-Access-Client-Id: YOUR_CLIENT_ID" \\
  -H "CF-Access-Client-Secret: YOUR_CLIENT_SECRET"

# 3. Cancel a running prediction
curl -X POST ${RELAY_URL}/v1/predictions/PREDICTION_ID/cancel \\
  -H "CF-Access-Client-Id: YOUR_CLIENT_ID" \\
  -H "CF-Access-Client-Secret: YOUR_CLIENT_SECRET"`;
  };

  const generatePython = () => {
    if (!selectedModel) return '';
    const cfg = getActiveConfig();
    const routedModel = effectiveModelId(selectedModel);
    const isTextModel = ['text-generation', 'conversational'].includes(selectedModel.pipeline);

    if (isTextModel && !cfg) {
      return `import requests

headers = {
    "Content-Type": "application/json",
    "CF-Access-Client-Id": "YOUR_CLIENT_ID",
    "CF-Access-Client-Secret": "YOUR_CLIENT_SECRET",
}

response = requests.post(
    "${RELAY_URL}/v1/chat/completions",
    headers=headers,
    json={
        "model": "${routedModel}",
        "messages": [
            ${systemMsg.trim() ? `{"role": "system", "content": "${systemMsg.replace(/"/g, '\\"')}"},\n            ` : ''}{"role": "user", "content": "${(userMsg || '...').replace(/"/g, '\\"')}"}
        ],
        "temperature": ${temperature},
        "max_tokens": ${maxTokens},
        "stream": ${streaming}
    }
)
print(response.json())`;
    }

    const input = cfg ? buildPredictionInput(cfg) : { prompt: userMsg || '...' };
    return `import requests, time

headers = {
    "Content-Type": "application/json",
    "CF-Access-Client-Id": "YOUR_CLIENT_ID",
    "CF-Access-Client-Secret": "YOUR_CLIENT_SECRET",
}
base = "${RELAY_URL}/v1/predictions"

# 1. Create the prediction — returns a unique id
job = requests.post(
    base,
    headers=headers,
    json=${JSON.stringify({ model: routedModel, input }, null, 4).replace(/^/gm, '    ').trim()}
).json()
prediction_id = job["id"]

# 2. Poll until terminal (succeeded / failed / canceled)
while True:
    time.sleep(1)
    job = requests.get(f"{base}/{prediction_id}", headers=headers).json()
    if job["status"] in ("succeeded", "failed", "canceled"):
        break
print(job)

# To cancel:  requests.post(f"{base}/{prediction_id}/cancel", headers=headers)`;
  };

  const filteredModels = searchQuery
    ? models.filter(m =>
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.fullId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.author.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : models;

  const getModelWorkers = (model) => model.workerCount || metrics?.models?.[model.fullId]?.active_workers || 0;

  const statusDotClass = {
    ready: 'bg-green-500 shadow-green-500/40',
    offline: 'bg-orange-500 shadow-orange-500/40',
    checking: 'bg-blue-500 shadow-blue-500/40',
  };

  const predSteps = ['queued', 'starting', 'processing', 'completed'];
  const cfg = getActiveConfig();
  const isTextModel = selectedModel && ['text-generation', 'conversational'].includes(selectedModel.pipeline);
  const useCustomInputs = !!cfg;

  // Split configured inputs: main area (textarea, text, image, video, audio, select) vs params panel (number, slider, toggle)
  const mainInputs = cfg?.inputs?.filter(inp => (inp.hardcoded === undefined || inp.hardcoded === null || inp.hardcoded === '') && ['textarea', 'text', 'image', 'video', 'audio', 'select'].includes(inp.type)) || [];
  const paramInputs = cfg?.inputs?.filter(inp => (inp.hardcoded === undefined || inp.hardcoded === null || inp.hardcoded === '') && ['number', 'slider', 'toggle'].includes(inp.type)) || [];

  return (
    <div className="flex h-[calc(100vh-180px)] border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* LEFT: Model Selector */}
      <div className="w-72 border-r border-gray-200 flex flex-col bg-white shrink-0">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-400">Models</span>
          <span className="bg-gray-100 px-2 py-0.5 rounded-full text-[10px] font-mono text-gray-500 border border-gray-200">
            {filteredModels.length}
          </span>
        </div>

        <div className="px-3 py-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search models..."
              className="w-full pl-8 pr-3 py-2 text-xs font-mono border border-gray-200 rounded-full bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-1 px-3 pb-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`font-mono text-[10.5px] px-2.5 py-1 rounded-full border transition-all ${
                activeFilter === f.key
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-1 scrollbar-thin">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-400">
              <RefreshCw size={20} className="animate-spin" />
              <span className="font-mono text-[11px]">Fetching models...</span>
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-8 text-gray-400 font-mono text-[11px]">No models found</div>
          ) : (
            filteredModels.map(m => {
              const workers = getModelWorkers(m);
              return (
                <div
                  key={m.id}
                  onClick={() => handleSelectModel(m)}
                  className={`px-3 py-2.5 rounded-lg cursor-pointer mb-0.5 border-[1.5px] transition-all ${
                    selectedModel?.id === m.id
                      ? 'bg-blue-50 border-blue-200'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[13px] font-medium text-gray-900 truncate">{m.playgroundConfig?.name || m.name}</span>
                    <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                      m.author === 'meta-llama' || m.author === 'meta' ? 'bg-indigo-50 text-indigo-600' :
                      m.author === 'mistralai' ? 'bg-orange-50 text-orange-600' :
                      m.author === 'deepseek-ai' ? 'bg-green-50 text-green-600' :
                      m.author === 'Qwen' ? 'bg-pink-50 text-pink-600' :
                      'bg-gray-50 text-gray-600'
                    }`}>{m.author?.substring(0, 12) || '?'}</span>
                  </div>
                  <div className="flex items-center gap-2 font-mono text-[10.5px] text-gray-400">
                    <span className="px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-[9.5px]">
                      {PIPELINE_LABELS[m.pipeline] || m.pipeline}
                    </span>
                    {m.downloads > 0 && <span>{m.downloads >= 1e6 ? (m.downloads/1e6).toFixed(1)+'M' : m.downloads >= 1e3 ? (m.downloads/1e3).toFixed(1)+'K' : m.downloads}</span>}
                    {m._tcf ? (
                      workers > 0
                        ? <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-green-50 text-green-600 border border-green-200">LIVE</span>
                        : <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-gray-50 text-gray-400 border border-gray-200">OFFLINE</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">HF</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* CENTER: Chat/Prompt */}
      <div className="flex-1 flex flex-col bg-gray-50 min-w-0">
        {/* Mode switcher */}
        <div className="flex items-center px-5 py-2.5 gap-1 border-b border-gray-200 bg-white">
          <button
            onClick={() => setActiveMode('chat')}
            className={`font-mono text-xs px-4 py-1.5 rounded-full border transition-all ${
              activeMode === 'chat'
                ? 'bg-gray-50 text-gray-900 border-gray-200'
                : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
          >Chat</button>
          <button
            onClick={() => setActiveMode('code')}
            className={`font-mono text-xs px-4 py-1.5 rounded-full border transition-all ${
              activeMode === 'code'
                ? 'bg-gray-50 text-gray-900 border-gray-200'
                : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
          >{'</>'} Code</button>
          <div className="flex-1" />
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-gray-400 px-3 py-1 border border-gray-200 rounded-full mr-2">
            <div className={`w-[7px] h-[7px] rounded-full shadow-md ${statusDotClass[serverStatus.status] || statusDotClass.checking}`} />
            <span>{serverStatus.text}</span>
          </div>
          <button
            onClick={isRunning ? cancelInference : runInference}
            disabled={!canRun() && !isRunning}
            className={`font-mono text-xs font-semibold px-5 py-2 rounded-full flex items-center gap-2 transition-all disabled:opacity-40 ${
              isRunning
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-900 text-white hover:bg-gray-800 hover:-translate-y-0.5 hover:shadow-lg'
            }`}
          >
            {isRunning ? <><Square size={12} /> Stop</> : <><Play size={12} /> Run</>}
            <span className="text-[9px] px-1.5 py-0.5 bg-white/20 rounded">{isRunning ? 'esc' : '\u2318\u21B5'}</span>
          </button>
        </div>

        {/* Chat UI */}
        {activeMode === 'chat' && (
          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3" ref={chatAreaRef}>
            {/* Text model without custom config: show system + user message */}
            {isTextModel && !useCustomInputs && (
              <>
                <div className="flex gap-3">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-amber-50 text-amber-600 border border-amber-200 shrink-0">system</span>
                  <div className="flex-1">
                    <textarea
                      value={systemMsg}
                      onChange={e => setSystemMsg(e.target.value)}
                      placeholder="You are a helpful assistant..."
                      rows={2}
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] leading-relaxed text-gray-900 resize-y outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-blue-50 text-blue-600 border border-blue-200 shrink-0">user</span>
                  <div className="flex-1">
                    <textarea
                      value={userMsg}
                      onChange={e => setUserMsg(e.target.value)}
                      onKeyDown={e => { if (e.metaKey && e.key === 'Enter') runInference(); }}
                      placeholder="Enter your message..."
                      rows={3}
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] leading-relaxed text-gray-900 resize-y outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Non-text model without custom config: simple prompt */}
            {!isTextModel && !useCustomInputs && (
              <div className="flex gap-3">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-blue-50 text-blue-600 border border-blue-200 shrink-0">prompt</span>
                <div className="flex-1">
                  <textarea
                    value={userMsg}
                    onChange={e => setUserMsg(e.target.value)}
                    onKeyDown={e => { if (e.metaKey && e.key === 'Enter') runInference(); }}
                    placeholder="Describe what you want to generate..."
                    rows={3}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] leading-relaxed text-gray-900 resize-y outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
            )}

            {/* Custom inputs from playground config */}
            {useCustomInputs && mainInputs.map(inp => (
              <div key={inp.name} className="flex gap-3">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-blue-50 text-blue-600 border border-blue-200 shrink-0 whitespace-nowrap">
                  {inp.label || inp.name}
                  {inp.required && <span className="text-red-400 ml-0.5">*</span>}
                </span>
                <div className="flex-1">
                  {inp.type === 'textarea' && (
                    <textarea
                      value={customInputs[inp.name] || ''}
                      onChange={e => updateCustomInput(inp.name, e.target.value)}
                      onKeyDown={e => { if (e.metaKey && e.key === 'Enter') runInference(); }}
                      placeholder={inp.label || inp.name}
                      rows={inp.name.includes('prompt') ? 3 : 2}
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] leading-relaxed text-gray-900 resize-y outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  )}
                  {inp.type === 'text' && (
                    <input
                      type="text"
                      value={customInputs[inp.name] || ''}
                      onChange={e => updateCustomInput(inp.name, e.target.value)}
                      onKeyDown={e => { if (e.metaKey && e.key === 'Enter') runInference(); }}
                      placeholder={inp.label || inp.name}
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  )}
                  {inp.type === 'select' && (
                    <select
                      value={customInputs[inp.name] || ''}
                      onChange={e => updateCustomInput(inp.name, e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 font-mono text-[13px] text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">Select {inp.label || inp.name}...</option>
                      {(inp.options || []).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}
                  {inp.type === 'image' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      {customInputs[inp.name] ? (
                        <div className="relative inline-block">
                          <img src={customInputs[inp.name]} alt="preview" className="max-h-32 rounded border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => updateCustomInput(inp.name, '')}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-700 font-mono text-xs">
                          <Upload size={14} />
                          <span>Upload image</span>
                          <input type="file" accept="image/*" className="hidden" onChange={e => handleFileInput(inp.name, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  )}
                  {inp.type === 'video' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      {customInputs[inp.name] ? (
                        <div className="relative inline-block">
                          <video src={customInputs[inp.name]} controls className="max-h-40 rounded border border-gray-200" />
                          <button
                            type="button"
                            onClick={() => updateCustomInput(inp.name, '')}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-700 font-mono text-xs">
                          <Upload size={14} />
                          <span>Upload video</span>
                          <input type="file" accept="video/*" className="hidden" onChange={e => handleFileInput(inp.name, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  )}
                  {inp.type === 'audio' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      {customInputs[inp.name] ? (
                        <div className="flex items-center gap-2">
                          <audio src={customInputs[inp.name]} controls className="h-8" />
                          <button
                            type="button"
                            onClick={() => updateCustomInput(inp.name, '')}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer text-gray-500 hover:text-gray-700 font-mono text-xs">
                          <Upload size={14} />
                          <span>Upload audio</span>
                          <input type="file" accept="audio/*" className="hidden" onChange={e => handleFileInput(inp.name, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Prediction tracker */}
            {predictionStatus && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] text-gray-400">Prediction</span>
                  <span className="font-mono text-[11px] text-gray-500 font-medium tabular-nums">{predictionTimer}s</span>
                </div>
                <div className="flex items-center gap-0">
                  {predSteps.map((step, i) => {
                    const currentIdx = predSteps.indexOf(predictionStatus.step);
                    const isDone = i < currentIdx;
                    const isActive = i === currentIdx;
                    const isError = predictionStatus.step === 'error' && i === currentIdx;
                    return (
                      <React.Fragment key={step}>
                        <div className={`flex items-center gap-1.5 font-mono text-[10.5px] whitespace-nowrap ${
                          isDone ? 'text-green-600' : isActive ? 'text-blue-600 font-semibold' : isError ? 'text-red-600' : 'text-gray-300'
                        }`}>
                          <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center text-[9px] ${
                            isDone ? 'border-green-500 bg-green-50 text-green-600' :
                            isActive ? 'border-blue-500 bg-blue-50' :
                            isError ? 'border-red-500 bg-red-50' :
                            'border-gray-200 bg-white'
                          }`}>
                            {isDone ? '✓' : isActive ? <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" /> : i + 1}
                          </div>
                          <span className="capitalize">{step}</span>
                        </div>
                        {i < predSteps.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-2 min-w-4 ${isDone ? 'bg-green-400' : isActive ? 'bg-gradient-to-r from-green-400 to-blue-400' : 'bg-gray-200'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error with retry */}
            {inferenceError && (
              <div className="flex gap-3">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-red-50 text-red-600 border border-red-200 shrink-0">error</span>
                <div className="flex-1">
                  <div className="bg-red-50 border border-red-200 rounded-lg px-5 py-4">
                    <p className="text-sm text-red-800">{inferenceError.message}</p>
                    {inferenceError.retryable && (
                      <button
                        onClick={() => { setInferenceError(null); setPredictionStatus(null); runInference(); }}
                        className="mt-3 bg-red-600 text-white text-xs font-medium px-4 py-1.5 rounded-md hover:bg-red-700 transition-colors inline-flex items-center gap-1.5"
                      >
                        <RefreshCw size={12} />
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Response */}
            {response && (
              <div className="flex gap-3">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full h-fit mt-0.5 bg-green-50 text-green-600 border border-green-200 shrink-0">output</span>
                <div className="flex-1">
                  <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 text-sm leading-relaxed text-gray-900 whitespace-pre-wrap">
                    {(response.includes('![') || response.includes('<video_url>') || response.includes('<audio_url>')) ? (
                      response.split('\n\n').map((line, i) => {
                        const imgMatch = line.match(/^!\[.*?\]\((.+?)\)$/);
                        if (imgMatch) return <OutputMedia key={i} src={imgMatch[1]} index={i} />;
                        const videoMatch = line.match(/^<video_url>(.+?)<\/video_url>$/);
                        if (videoMatch) return <video key={i} src={videoMatch[1]} controls autoPlay loop className="max-w-full rounded-lg border border-gray-200 mb-2" />;
                        const audioMatch = line.match(/^<audio_url>(.+?)<\/audio_url>$/);
                        if (audioMatch) return <audio key={i} src={audioMatch[1]} controls className="w-full mb-2" />;
                        return <span key={i}>{line}</span>;
                      })
                    ) : (
                      <>
                        {response}
                        {isRunning && <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 align-text-bottom animate-pulse" />}
                      </>
                    )}
                  </div>
                  {responseMeta && (
                    <div className="flex items-center gap-3 mt-2 pt-2 font-mono text-[10.5px] text-gray-400 flex-wrap">
                      <span>{responseMeta.latency}</span>
                      {responseMeta.tokensIn && <span>{responseMeta.tokensIn} in</span>}
                      <span>{responseMeta.tokensOut} out</span>
                      <span>{responseMeta.tps} tok/s</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Code view */}
        {activeMode === 'code' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <CodeBlock language="curl" code={generateCurl()} />
            <CodeBlock language="python" code={generatePython()} />
          </div>
        )}
      </div>

      {/* RIGHT: Parameters */}
      <div className="w-80 border-l border-gray-200 flex flex-col overflow-y-auto bg-white shrink-0">
        <div className="px-4 py-3 border-b border-gray-200">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-400">Parameters</span>
        </div>

        {/* Standard text model params (no custom config) */}
        {isTextModel && !useCustomInputs && (
          <>
            <div className="p-4 border-b border-gray-200 space-y-4">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Generation</span>
              <ParamSlider label="Temperature" value={temperature} onChange={setTemperature} min={0} max={2} step={0.05} />
              <ParamSlider label="Max Tokens" value={maxTokens} onChange={setMaxTokens} min={64} max={8192} step={64} />
              <ParamSlider label="Top P" value={topP} onChange={setTopP} min={0} max={1} step={0.01} />
            </div>
            <div className="p-4 border-b border-gray-200 space-y-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Options</span>
              <div className="flex items-center justify-between py-1">
                <span className="font-mono text-xs text-gray-500">Stream response</span>
                <button
                  onClick={() => setStreaming(!streaming)}
                  className={`w-9 h-5 rounded-full relative transition-all ${streaming ? 'bg-gray-900' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-[3px] left-[3px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${streaming ? 'translate-x-4' : ''}`} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Custom params from playground config */}
        {useCustomInputs && paramInputs.length > 0 && (
          <div className="p-4 border-b border-gray-200 space-y-4">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Model Parameters</span>
            {paramInputs.map(inp => {
              if (inp.type === 'slider') {
                return (
                  <ParamSlider
                    key={inp.name}
                    label={inp.label || inp.name}
                    value={customInputs[inp.name] ?? inp.min ?? 0}
                    onChange={v => updateCustomInput(inp.name, v)}
                    min={inp.min ?? 0}
                    max={inp.max ?? 100}
                    step={inp.step ?? 1}
                  />
                );
              }
              if (inp.type === 'number') {
                return (
                  <div key={inp.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-gray-500">{inp.label || inp.name}</span>
                      {inp.required && <span className="text-[9px] text-red-400">required</span>}
                    </div>
                    <input
                      type="number"
                      value={customInputs[inp.name] ?? ''}
                      onChange={e => updateCustomInput(inp.name, e.target.value ? parseFloat(e.target.value) : '')}
                      min={inp.min}
                      max={inp.max}
                      step={inp.step}
                      placeholder={inp.label || inp.name}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                );
              }
              if (inp.type === 'toggle') {
                return (
                  <div key={inp.name} className="flex items-center justify-between py-1">
                    <span className="font-mono text-xs text-gray-500">{inp.label || inp.name}</span>
                    <button
                      onClick={() => updateCustomInput(inp.name, !customInputs[inp.name])}
                      className={`w-9 h-5 rounded-full relative transition-all ${customInputs[inp.name] ? 'bg-gray-900' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-[3px] left-[3px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${customInputs[inp.name] ? 'translate-x-4' : ''}`} />
                    </button>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}

        {/* No custom config for non-text model: show empty state */}
        {!isTextModel && !useCustomInputs && (
          <div className="p-4 border-b border-gray-200">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Model Parameters</span>
            <p className="text-xs text-gray-400 mt-2">No configurable parameters for this model.</p>
          </div>
        )}

        {/* Operator note from the playground config: compatible resolutions,
            parameter limits, anything worth reading before spending a run.
            Sits under the settings it qualifies, above the call it explains. */}
        {cfg?.notes && String(cfg.notes).trim() && (
          <div className="p-4 border-b border-gray-200">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Notes</span>
            <p className="mt-2 text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap">{String(cfg.notes).trim()}</p>
          </div>
        )}

        {/* API example — lives with the parameters it mirrors, so a change
            to a field is visible in the call immediately below it. */}
        {selectedModel && (
          <div className="p-4 border-b border-gray-200">
            <details className="group">
              <summary className="cursor-pointer select-none flex items-center justify-between">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">API call</span>
                <span className="font-mono text-[10px] text-gray-400 group-open:hidden">show</span>
                <span className="font-mono text-[10px] text-gray-400 hidden group-open:inline">hide</span>
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-[10.5px] text-gray-400 leading-relaxed">
                  Replace <code className="bg-gray-100 px-1 rounded">&lt;api key&gt;</code> with your key
                  (Settings &rarr; API Keys). Unfilled fields stay as
                  <code className="bg-gray-100 px-1 rounded ml-1">$(FIELD)</code> — fill one above and it
                  appears here.
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generateApiSnippet());
                    setApiSnippetCopied(true);
                    setTimeout(() => setApiSnippetCopied(false), 1500);
                  }}
                  className="w-full font-mono text-[10.5px] px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600"
                >
                  {apiSnippetCopied ? 'copied' : 'copy'}
                </button>
                {/* An embedded workflow makes this body long, so it scrolls in place
                    rather than pushing the routing stats off the column. */}
                <pre className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-2 text-[10px] leading-relaxed font-mono text-gray-800 max-h-96 overflow-auto whitespace-pre">{generateApiSnippet()}</pre>
              </div>
            </details>
          </div>
        )}

        {/* Routing stats */}
        {selectedModel && metrics && (
          <div className="p-4 space-y-3">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">Current Routing</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Workers', value: selectedModel.workerCount || metrics.models?.[selectedModel.fullId]?.active_workers || '0' },
                { label: 'In-flight', value: metrics.models?.[selectedModel.fullId]?.in_flight || '0' },
                { label: 'Requests', value: metrics.models?.[selectedModel.fullId]?.requests || '0' },
                { label: 'Cost', value: selectedModel.pricing ? `$${(selectedModel.pricing.input / 1000).toFixed(4)} in` : '--' },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-md p-2.5 border border-gray-200">
                  <div className="font-mono text-[9px] text-gray-400 uppercase tracking-wide mb-1">{s.label}</div>
                  <div className={`font-mono text-sm font-semibold ${s.label === 'Workers' && parseInt(s.value) > 0 ? 'text-green-600' : 'text-gray-900'}`}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Param slider component
const ParamSlider = ({ label, value, onChange, min, max, step }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-xs text-gray-500">{label}</span>
      <span className="font-mono text-xs text-blue-600 font-semibold min-w-[40px] text-right">{value}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full h-[3px] rounded bg-gray-200 appearance-none cursor-pointer accent-gray-900"
    />
  </div>
);

// Code block component
const CodeBlock = ({ language, code }) => (
  <div className="rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-800 border-b border-gray-700">
      <span className="font-mono text-[11px] text-gray-400">{language}</span>
      <button
        onClick={() => navigator.clipboard.writeText(code)}
        className="font-mono text-[10px] px-3 py-1 rounded-full border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 transition-colors flex items-center gap-1"
      >
        <Copy size={10} /> Copy
      </button>
    </div>
    <pre className="p-4 bg-gray-900 font-mono text-xs leading-relaxed text-gray-200 overflow-x-auto whitespace-pre">
      {code}
    </pre>
  </div>
);

export default PlaygroundTab;
